import React from 'react';
import { Formik, Form, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import { Auth, CognitoHostedUIIdentityProvider } from '@aws-amplify/auth';
import { FaGoogle } from '@react-icons/all-files/fa/FaGoogle';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { FieldError } from '@/components/ui/field';
import { FormFields } from '@/components/Inputs/FormFields';
import { makeFormFieldCreator } from '@/components/Inputs/FieldCreator';
import { abandonMerge, beginMergeSignIn } from '@/utils/identityClaimSession';
import { GENERIC_ERROR_MESSAGE, signInForMerge } from './identityClaimCognito';

const SAME_USER_MESSAGE =
  'Entraste con este mismo usuario. Entrá con el usuario que tiene ese email.';

type SignInValues = { username: string; password: string };
const fieldCreator = makeFormFieldCreator<SignInValues>();
const formFields = [
  fieldCreator({
    name: 'username',
    label: 'Usuario o email',
    inputProps: { autoComplete: 'username' },
  }),
  fieldCreator({
    name: 'password',
    label: 'Clave',
    inputProps: { type: 'password', autoComplete: 'current-password' },
  }),
];
const formSchema = Yup.object().shape({
  username: Yup.string().required('Campo obligatorio'),
  password: Yup.string().required('Campo obligatorio'),
});

export const IdentityClaimMergeStep: React.FC<{ requestedEmail: string }> = ({
  requestedEmail,
}) => {
  const [error, setError] = React.useState<string | null>(null);

  const handleGoogle = async () => {
    setError(null);
    try {
      beginMergeSignIn();
      await Auth.federatedSignIn({
        provider: CognitoHostedUIIdentityProvider.Google,
      });
    } catch {
      abandonMerge();
      setError(GENERIC_ERROR_MESSAGE);
    }
  };

  const handlePassword = async (
    values: SignInValues,
    { setSubmitting }: FormikHelpers<SignInValues>
  ) => {
    setError(null);
    try {
      beginMergeSignIn();
    } catch {
      setError(GENERIC_ERROR_MESSAGE);
      setSubmitting(false);
      return;
    }
    const failure = await signInForMerge(
      values.username,
      values.password,
      SAME_USER_MESSAGE
    );
    if (!failure) return;
    setError(failure);
    setSubmitting(false);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Ese email ya tiene un usuario</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <p className="text-sm">
          {`Si ${requestedEmail} es tuyo, entrá con ese usuario y los unimos: te queda uno solo, con tus partes, tus máquinas y tus roles.`}
        </p>
        <Button type="button" variant="outline" onClick={handleGoogle}>
          <FaGoogle />
          Entrar con Google
        </Button>
        <Formik<SignInValues>
          initialValues={{ username: requestedEmail, password: '' }}
          validationSchema={formSchema}
          onSubmit={handlePassword}
        >
          {({ isSubmitting }) => (
            <Form className="flex flex-col gap-4">
              <FormFields fields={formFields} />
              <Button type="submit" disabled={isSubmitting}>
                Entrar con clave
              </Button>
            </Form>
          )}
        </Formik>
        {error && <FieldError>{error}</FieldError>}
      </CardContent>
    </Card>
  );
};
